import usePrevious from '@liveauctioneers/caterwaul-components/lib/hooks/usePrevious';
import useVisibilitySensor from '@rooks/use-visibility-sensor';
import { useEffect, useRef, useState } from 'react';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { trackFeaturedCatalogView } from '@/redux/modules/analytics';
import { getCatalogSellerId, getShouldShowLive } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getLiveCatalogStatus } from '@/redux/modules/console';
import { fetchCatalogPresence, fetchLiveCatalogStatusIfNeeded } from '@/redux/modules/liveCatalogStatus';

type Props = {
    cardRef: ReturnType<typeof useRef<HTMLDivElement>>;
    catalogId: number;
};

export const useTrackCardVisibility = ({ cardRef, catalogId }: Props) => {
    const dispatch = useAppDispatch();

    const [hasTrackedVisibility, setHasTrackedVisibility] = useState(false);
    const { isVisible } = useVisibilitySensor(cardRef, {
        intervalCheck: 750,
        resizeCheck: true,
        scrollCheck: true,
    });
    const wasVisible = usePrevious(isVisible);

    const liveStatus = useAppSelector((state) => getLiveCatalogStatus(state, catalogId));
    const shouldShowLive = useAppSelector((state) => getShouldShowLive(state, catalogId));
    const isLive = shouldShowLive || liveStatus === 'live';

    const sellerId = useAppSelector((state) => getCatalogSellerId(state, catalogId));

    useEffect(() => {
        if (!wasVisible && isVisible && !hasTrackedVisibility) {
            dispatch(trackFeaturedCatalogView({ catalogId })).then(() => {
                setHasTrackedVisibility(true);
                // Fetch live catalog status from auction-engine if needed
                if (isLive) {
                    dispatch(fetchLiveCatalogStatusIfNeeded(catalogId, sellerId));
                    dispatch(fetchCatalogPresence(catalogId));
                }
            });
        }
    }, [catalogId, dispatch, hasTrackedVisibility, isLive, isVisible, sellerId, wasVisible]);
};
